// BaseAreaOfRegularPyramidTask.js
// Zadanie 3: Ostrosłup prawidłowy (trójkątny/czworokątny/sześciokątny).
// Dana długość krawędzi podstawy a. Oblicz pole podstawy P_p.
// Input: RootFractionInput (wygodny dla ułamków przy √3).
// Sprawdzanie: dopuszczamy przybliżenie do 0.01 (bez pisania o tym w treści).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function nearly(a, b, eps = 0.01) {
  return Math.abs(a - b) <= eps;
}

function round2(x) {
  return Math.round(x * 100) / 100;
}

// ładny zapis dokładny (LaTeX) – bez zewnętrznych klamerek
function exactLatex(baseType, a) {
  const a2 = a * a;

  if (baseType === "square") {
    return `${a2}`;
  }

  // trójkąt: (√3/4) a^2 = (a^2/4)√3  albo  (a^2√3)/4
  if (baseType === "triangle") {
    if (a2 % 4 === 0) return `${a2 / 4}\\sqrt{3}`;
    return `\\dfrac{${a2}\\sqrt{3}}{4}`;
  }

  // sześciokąt: 6*(√3/4)a^2 = (3√3/2)a^2 = (3a^2/2)√3
  const num = 3 * a2;
  if (num % 2 === 0) return `${num / 2}\\sqrt{3}`;
  return `\\dfrac{${num}\\sqrt{3}}{2}`;
}

export function generateTask() {
  const baseType = pick(["triangle", "square", "hexagon"]);
  const a = pick([3, 4, 5, 6, 8, 10, 12]);

  const a2 = a * a;

  let areaNumeric = 0;
  let humanBase = "";

  if (baseType === "square") {
    areaNumeric = a2;
    humanBase = "czworokątny";
  } else if (baseType === "triangle") {
    areaNumeric = (Math.sqrt(3) / 4) * a2;
    humanBase = "trójkątny";
  } else {
    areaNumeric = 6 * (Math.sqrt(3) / 4) * a2;
    humanBase = "sześciokątny";
  }

  const exactAnsLatex = exactLatex(baseType, a);

  return {
    type: "BaseAreaOfRegularPyramidTask",
    data: {
      baseType,
      humanBase,
      a,
      a2,
      areaNumeric,
      exactAnsLatex,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      Ostrosłup <b>prawidłowy {data.humanBase}</b> ma krawędź podstawy długości{" "}
      <InlineMath math={`{a=${data.a}}`} />. Oblicz pole podstawy{" "}
      <InlineMath math={`{P_p}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  // U Ciebie poprawione było, że symbol jest "wyżej" — więc robimy to tak samo:
  // osobna linia z P_p =, a pod spodem input.
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <InlineMath math={`{P_p =}`} />
      </div>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P_p" // ważne: podgląd w UI będzie "P_p = ..."
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  // RootFractionInput zwykle daje { raw, parsed }, ale robimy fallback dla pewności
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;

  const parsed =
    typeof userAnswer === "object" && userAnswer?.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (typeof parsed !== "number" || !Number.isFinite(parsed)) return false;

  return nearly(parsed, data.areaNumeric, 0.01);
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  // spójny, ładny zapis jak w podglądzie inputu
  return <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, "P_p")}`} />;
}

export function renderAnswer({ data }) {
  // konkretna odpowiedź: P_p = ...
  return <InlineMath math={`{P_p=${data.exactAnsLatex}}`} />;
}

export function renderExplanation({ data }) {
  const { baseType, a, a2, areaNumeric, exactAnsLatex } = data;
  const approx2 = round2(areaNumeric);

  if (baseType === "square") {
    return (
      <div style={{ display: "grid", rowGap: 10 }}>
        <p>
          <strong>Krok 1.</strong> Podstawa to kwadrat, więc:
        </p>
        <BlockMath math={`{P_p=a^2}`} />

        <p>
          <strong>Krok 2.</strong> Podstawiamy <InlineMath math={`{a=${a}}`} />:
        </p>
        <BlockMath math={`{P_p=${a}^2=${a2}}`} />

        <p>
          <strong>Wynik dokładny:</strong> <InlineMath math={`{P_p=${exactAnsLatex}}`} />
        </p>

        <p style={{ opacity: 0.85 }}>
          <strong>Wartość przybliżona:</strong> <InlineMath math={`{${approx2}}`} />
        </p>
      </div>
    );
  }

  if (baseType === "triangle") {
    // pokazujemy przemnożenie i ewentualne uproszczenie
    const step1 = `P_p=\\dfrac{\\sqrt3}{4}a^2`;
    const step2 = `P_p=\\dfrac{\\sqrt3}{4}\\cdot ${a}^2=\\dfrac{\\sqrt3}{4}\\cdot ${a2}`;
    const step3 =
      a2 % 4 === 0
        ? `P_p=\\dfrac{${a2}}{4}\\sqrt3=${a2 / 4}\\sqrt3`
        : `P_p=\\dfrac{${a2}\\sqrt3}{4}`;

    return (
      <div style={{ display: "grid", rowGap: 10 }}>
        <p>
          <strong>Krok 1.</strong> Podstawa to trójkąt równoboczny. Wzór na pole:
        </p>
        <BlockMath math={`{${step1}}`} />

        <p>
          <strong>Krok 2.</strong> Podstawiamy <InlineMath math={`{a=${a}}`} />:
        </p>
        <BlockMath math={`{${step2}}`} />

        <p>
          <strong>Krok 3.</strong> Uproszczenie:
        </p>
        <BlockMath math={`{${step3}}`} />

        <p>
          <strong>Wynik dokładny:</strong> <InlineMath math={`{P_p=${exactAnsLatex}}`} />
        </p>

        <p style={{ opacity: 0.85 }}>
          <strong>Wartość przybliżona:</strong> <InlineMath math={`{${approx2}}`} />
        </p>
      </div>
    );
  }

  // hexagon
  const step1 = `P_p=6\\cdot \\dfrac{\\sqrt3}{4}a^2`;
  const step2 = `P_p=6\\cdot \\dfrac{\\sqrt3}{4}\\cdot ${a}^2=6\\cdot \\dfrac{\\sqrt3}{4}\\cdot ${a2}`;
  const step3 = `P_p=\\dfrac{6\\cdot ${a2}}{4}\\sqrt3`;
  const step4 = `P_p=\\dfrac{3\\cdot ${a2}}{2}\\sqrt3`;

  // końcowy zapis (czasem da się zrobić całkowity współczynnik)
  const num = 3 * a2;
  const step5 = num % 2 === 0 ? `P_p=${num / 2}\\sqrt3` : `P_p=\\dfrac{${num}\\sqrt3}{2}`;

  return (
    <div style={{ display: "grid", rowGap: 10 }}>
      <p>
        <strong>Krok 1.</strong> Podstawa to sześciokąt foremny. Można go rozłożyć
        na 6 trójkątów równobocznych, więc:
      </p>
      <BlockMath math={`{${step1}}`} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy <InlineMath math={`{a=${a}}`} />:
      </p>
      <BlockMath math={`{${step2}}`} />

      <p>
        <strong>Krok 3.</strong> Porządkujemy współczynnik liczbowy:
      </p>
      <BlockMath math={`{${step3}}`} />
      <BlockMath math={`{${step4}}`} />

      <p>
        <strong>Krok 4.</strong> Zapis wyniku:
      </p>
      <BlockMath math={`{${step5}}`} />

      <p>
        <strong>Wynik dokładny:</strong> <InlineMath math={`{P_p=${exactAnsLatex}}`} />
      </p>

      <p style={{ opacity: 0.85 }}>
        <strong>Wartość przybliżona:</strong> <InlineMath math={`{${approx2}}`} />
      </p>
    </div>
  );
}
